import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getFinancialStatementBook8Column } from '../../../../../../lib/api/accounting';
import GlobalSearcher from '../../../../../../lib/common/GlobalSearcher';
import CustomButton from '../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import { RootState } from '../../../../../../services/redux/store';
import { useAccountingPeriod } from '../../context/AccountingPeriodContext';
import { fetchWithDateChunking } from '../utils/fetchWithDateChunking';
import {
  deepMergeGroupedData,
  mergeSummedLines,
} from '../utils/mergeStrategies';
import FinancialStatementBookEntriesTable from './components';

dayjs.extend(utc);

import ExcelJS from 'exceljs';
import { formatDate } from 'lib/functions/utils';

const exportToExcel = async (
  entries: any[],
  startDate: Date,
  endDate: Date,
  companyIdentifier?: string,
  companyName?: string,
) => {
  // Mapeo de datos planos
  const plainData = entries.map((line: any) => {
    return [
      line.account, // Fecha
      line.accountName, // Rut cliente
      line.debit, // Débito
      line.credit, // Crédito
      line.debtor, // Débito final
      line.creditor, // Crédito final
      line.active, // Activo
      line.passive, // Pasivo
      line.loss, // Pérdida
      line.income, // Ingreso
    ];
  });
  const totals = plainData.reduce(
    (acc: any, line: any) => {
      acc.debit += line[2];
      acc.credit += line[3];
      acc.debtor += line[4];
      acc.creditor += line[5];
      acc.active += line[6];
      acc.passive += line[7];
      acc.loss += line[8];
      acc.income += line[9];
      return acc;
    },
    {
      debit: 0,
      credit: 0,
      debtor: 0,
      creditor: 0,
      active: 0,
      passive: 0,
      loss: 0,
      income: 0,
    },
  );

  const sums = [
    'Sumas totales',
    '',
    totals.debit,
    totals.credit,
    totals.debtor,
    totals.creditor,
    totals.active,
    totals.passive,
    totals.loss,
    totals.income,
  ];

  const result = [
    'Resultado',
    '',
    '',
    '',
    '',
    '',
    totals.active < totals.passive ? totals.passive - totals.active : 0,
    totals.passive < totals.active ? totals.active - totals.passive : 0,
    totals.loss < totals.income ? totals.income - totals.loss : 0,
    totals.income < totals.loss ? totals.loss - totals.income : 0,
  ];

  const equals = [
    'Totales iguales',
    '',
    totals.debit,
    totals.credit,
    totals.debtor,
    totals.creditor,
    totals.active > totals.passive ? totals.active : totals.passive,
    totals.passive > totals.active ? totals.passive : totals.active,
    totals.loss > totals.income ? totals.loss : totals.income,
    totals.income > totals.loss ? totals.income : totals.loss,
  ];

  // Encabezados de la hoja de Excel
  const headers = [
    'Cuenta',
    'Nombre de Cuenta',
    'Debe',
    'Haber',
    'Débito',
    'Crédito',
    'Activo',
    'Pasivo',
    'Pérdida',
    'Ganancia',
  ];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sheet1');

  // Filas de información inicial
  const infoRows = [
    ['Informacion de la consulta', ''],
    ['Razón social:', companyName || 'N/A'],
    ['RUT:', companyIdentifier || 'N/A'],
    ['Tipo de balance:', '8 Columnas'],
    ['Periodo:', `${formatDate(startDate)} a ${formatDate(endDate)}`],
    ['Fecha de consulta:', formatDate(new Date())],
    [], // Fila vacía como separador
  ];

  // Agregar filas de información
  infoRows.forEach((rowData) => {
    const row = worksheet.addRow(rowData);
    // Formato para las filas de información (primera columna en negrita)
    if (rowData.length > 0) {
      const firstCell = row.getCell(1);
      firstCell.font = { bold: true };
    }
  });

  const rows = [headers, ...plainData, sums, result, equals];

  // Agregar datos a la hoja
  rows.forEach((rowData, rowIndex) => {
    const row = worksheet.addRow(rowData);

    if (rowIndex === 0) {
      // Formato para la primera fila (encabezados)
      row.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        worksheet.getColumn(cell.col).width = 20; // Ajustar ancho de columna
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFDDDDDD' }, // Color gris claro
        };
      });
    }
  });

  // Guardar el archivo
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);

  // Descargar el archivo
  const a = document.createElement('a');
  a.href = url;
  a.download = `Balance_General_${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;
  a.click();

  URL.revokeObjectURL(url);
};

const FinancialStatementBook: React.FC<{
  transactional?: boolean;
  startDate?: Date;
  endDate?: Date;
}> = ({
  transactional = false,
  startDate: propStartDate,
  endDate: propEndDate,
}) => {
  const {
    startDate: contextStart,
    endDate: contextEnd,
    setPeriod,
  } = useAccountingPeriod();
  const hasProps = propStartDate != null && propEndDate != null;

  const [localStartDate, setLocalStartDate] = useState<string | null>(
    hasProps && propStartDate
      ? propStartDate.toISOString().split('T')[0]
      : contextStart,
  );
  const [localEndDate, setLocalEndDate] = useState<string | null>(
    hasProps && propEndDate
      ? propEndDate.toISOString().split('T')[0]
      : contextEnd,
  );

  const startDate = hasProps ? localStartDate : contextStart;
  const endDate = hasProps ? localEndDate : contextEnd;

  const [data, setData] = useState<any>({ summedLines: [] });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  const setUp = async (
    startDateParam: string,
    endDateParam: string,
    showLoading: boolean = true,
  ) => {
    if (!hasProps) {
      setPeriod(startDateParam, endDateParam);
    } else {
      setLocalStartDate(startDateParam);
      setLocalEndDate(endDateParam);
    }

    try {
      if (showLoading) setLoading(true);
      const result = await fetchWithDateChunking({
        startDate: startDateParam,
        endDate: endDateParam,
        thresholdMonths: 6,
        apiFunction: getFinancialStatementBook8Column,
        mergeStrategy: (responses) => {
          const allSummedResponses = responses.map(
            (response) => response?.summedLines || [],
          );
          const allGroupedResponses = responses.map(
            (response) => response?.groupedByCategoryAndBusiness || {},
          );

          const mergedSummedLines = mergeSummedLines(allSummedResponses);
          const sortedMergedSummedLines = mergedSummedLines.sort((a, b) =>
            a.account.localeCompare(b.account),
          );

          return {
            summedLines: sortedMergedSummedLines,
            groupedByCategoryAndBusiness:
              deepMergeGroupedData(allGroupedResponses),
          };
        },
        additionalParams: { transactional },
        dispatch,
      });

      setData(result);
    } catch (error) {
      console.error('Error fetching financial statement book:', error);
    }
    if (showLoading) setLoading(false);
  };

  const refreshData = async (showLoading: boolean = true) => {
    if (startDate && endDate) {
      await setUp(startDate, endDate, showLoading);
    }
  };

  useEffect(() => {
    if (hasProps && propStartDate && propEndDate) {
      const startDateString = propStartDate.toISOString().split('T')[0];
      const endDateString = propEndDate.toISOString().split('T')[0];
      setLocalStartDate(startDateString);
      setLocalEndDate(endDateString);
      void setUp(startDateString, endDateString);
    } else if (!hasProps && contextStart && contextEnd) {
      void setUp(contextStart, contextEnd);
    }
  }, [
    hasProps,
    propStartDate?.getTime(),
    propEndDate?.getTime(),
    contextStart,
    contextEnd,
  ]);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <div>
        <HeaderWithActions
          title={`Período: ${startDate ? dayjs(startDate).format('DD/MM/YYYY') : ''} - ${endDate ? dayjs(endDate).format('DD/MM/YYYY') : ''}`}
          icon={<CalendarMonthIcon color="primary" />}
          variant="body1"
          item={
            <div
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}
            >
              <div>
                <GlobalSearcher
                  filters={[
                    {
                      type: 'date',
                      label: 'Fecha de inicio',
                      dateKey: 'startDate',
                      value: startDate,
                      setter: (val) => {
                        if (hasProps) setLocalStartDate(val);
                        else setPeriod(val, endDate);
                      },
                    },
                    {
                      type: 'date',
                      label: 'Fecha de fin',
                      dateKey: 'endDate',
                      value: endDate,
                      setter: (val) => {
                        if (hasProps) setLocalEndDate(val);
                        else setPeriod(startDate, val);
                      },
                    },
                  ]}
                  triggerSearch={async () => {
                    if (startDate && endDate) {
                      await setUp(startDate, endDate);
                    }
                  }}
                  shortcuts={true}
                />
              </div>
              <div>
                <CustomButton
                  color="primary"
                  onClick={() => {
                    if (startDate && endDate) {
                      void exportToExcel(
                        data.summedLines,
                        new Date(startDate),
                        new Date(endDate),
                        user?.businessIdentifier,
                        user?.companyName,
                      );
                    }
                  }}
                >
                  Exportar
                </CustomButton>
              </div>
            </div>
          }
        />
      </div>
      <FinancialStatementBookEntriesTable
        entries={data}
        refreshData={refreshData}
      />
    </div>
  );
};

export default FinancialStatementBook;
